import { TableData, TableSort } from 'app/components/table/tabledata';

import { CurrentUser } from 'app/components/data/core/models/base/user';
import { Order, OrderStatus } from 'app/components/data/core/models/order/order';
import { PFQuery } from 'app/components/data/core/models/base/query';
import * as AccessQueries from 'app/components/access/query';
import { ServerTableDataSource } from 'app/components/table/data_sources/server-data-source';

import { ConfigurationService } from "app/main/admin/settings/configuration.service";
import { OrderColumns } from '../../helpers/order-columns-factory';
import {TableNameKeys} from "../../../../components/table/data_sources/table-name-keys";
import { OrderQuerySearchFilter } from '../../helpers/order-query-search-filter';

export class FinishedOrdersTabledata extends TableData {
  constructor(
    private configurationService: ConfigurationService
  ) {
    super();

    let columns = [
      OrderColumns.makeColumnNumber()
    ];

    let userColumns;
    if (CurrentUser.isCustomer()) {
      userColumns = [
        OrderColumns.makeColumnUnloadPoint(),
        OrderColumns.makeColumnBrand(),
        OrderColumns.makeColumnUnloadedTonnage(),
        OrderColumns.makeColumnFacticalUnloadDate(),
        OrderColumns.makeColumnDriver(),      
        OrderColumns.makeColumnVehicleNumber() 
      ];
    } else {
      userColumns = [
        OrderColumns.makeColumnCustomer(),
        OrderColumns.makeColumnUnloadPoint(),
        OrderColumns.makeColumnFacticalUnloadDate(),
        OrderColumns.makeColumnUnloadedTonnage(),
        OrderColumns.makeColumnFacticalLoadDate(),
        OrderColumns.makeColumnSupplier(),
        OrderColumns.makeColumnDriver(),      
        OrderColumns.makeColumnVehicleNumber()
      ];
    }
    userColumns.push(OrderColumns.makeColumnComment());

    columns = columns.concat(userColumns);
    this.setColumns(columns);

    let includeFields = [
      'unloadingPoint',
      'offers.trip.historyRecords',
      'offers.trip.transportUnit.driver',
      'offers.transportUnit.driver',
      'offers.transportUnit.user',
      'offers.transportUnit.vehicle.model.brand',
      'offers.transportUnit.vehicle',
      'offers.transportUnit.trailer',
      'articleBrand.type',
      'customer',
      'loadingEntrance',
      'unloadingEntrance',
      'carriers',
      'carrierOffers.driver',
    ];

    if (CurrentUser.isCustomer()) {
      includeFields.push('unloadingPoint.manager');
    } else {
      includeFields = includeFields.concat([
        'loadingPoint',
        'intermediary',
        'supplier',
        'loadingAgreement',
      ]);
    }

    let sort: TableSort = new TableSort('lastProgressDate', 'desc');
    let tableDataSource = new ServerTableDataSource('Order', includeFields);
    tableDataSource.setDefaultSort(sort);

    tableDataSource.queryFilter = (filter: string) => {
      const searchValue = (filter || '').trim();
      if (!searchValue) {
        return new PFQuery('Order');
      }

      const searchQueries = [
        OrderQuerySearchFilter.driverName(searchValue),
        OrderQuerySearchFilter.vehicleNumber(searchValue),
        OrderQuerySearchFilter.supplierName(searchValue),
        OrderQuerySearchFilter.customerName(searchValue),
        OrderQuerySearchFilter.unloadingEntranceAddress(searchValue),
      ];

      const orderNumber = parseFloat(searchValue);
      if (!isNaN(orderNumber)) {
        searchQueries.push(OrderQuerySearchFilter.orderNumber(orderNumber));
      }

      return PFQuery.or(...searchQueries);
    };

    function applyQueryModification(query: PFQuery): PFQuery {
      if (CurrentUser.isCustomer()) {
        query = AccessQueries.containsCurrentCustomer(query);
      } else if (CurrentUser.isDispatcher()) {
        query = AccessQueries.containsCurrentCarrier(query);
      }
      else if (!CurrentUser.isAdministrator() && !CurrentUser.isLogistician()) {
        query = AccessQueries.authorMatchesCurrentUser(query);
        let managerAssignedOrders = new PFQuery('Order');
        managerAssignedOrders = AccessQueries.managerMatchesCurrentUser(managerAssignedOrders);
        query = PFQuery.or(query, managerAssignedOrders);
      }
      query.equalTo('unfinishedTonnage', 0);
      query = AccessQueries.filterDeleted(query);
      query.equalTo('status', OrderStatus.READY);
      return query;
    }

    tableDataSource.queryModify = applyQueryModification;
    this.setProvider(tableDataSource);
  }

  canSortColumn(column) {
    switch (column.key) {
      case TableNameKeys.number:
      case TableNameKeys.loadDateFact:
      case TableNameKeys.unloadDateFact:
      case TableNameKeys.customer:
      case TableNameKeys.tonnageFact:
        return true;
      default:
        return false;
    }
  }

  canSortByDateColumn(column) {
    switch (column.key) {
      case TableNameKeys.unloadDateFact:
      case TableNameKeys.loadDateFact:
      case TableNameKeys.customer:
      case TableNameKeys.supplier:
        return false;
      default:
        return true;
    }
  }

  isWarnRow(order: Order) {
    if (CurrentUser.isCustomer()) return false;
    if (order.isDeleted()) return false;
    let conf = this.configurationService.getConfiguration();
    if (!conf) return false;
    let maxDistance = conf.getMaxDistanceDifference() / 1000;
    return order.isDifferentUnloadingPoint(maxDistance) || order.isDifferentLoadingPoint(maxDistance);
  }
}
